import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AdminTwilio() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(r => setS(r.data || {})); }, []);
  if (!s) return <div className="text-ink-muted">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const ready = !!(s.twilio_account_sid && s.twilio_auth_token && s.twilio_from_number);

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <div className="overline">Integrations</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Twilio · Mobile OTP</h1>
        <p className="text-sm text-ink-muted mt-1">When OFF, the loan application wizard skips OTP entirely. When ON, customers must verify their mobile via real Twilio SMS.</p>
      </div>

      <div className="card-flat p-6 sm:p-7 space-y-5">
        <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4">
          <div>
            <div className="font-semibold">Enable real OTP via Twilio</div>
            <div className="text-xs text-ink-muted">Toggle requires valid Twilio credentials below.</div>
          </div>
          <Switch data-testid="twilio-toggle" checked={!!s.otp_real_enabled} onCheckedChange={(v)=>set("otp_real_enabled", v)}/>
        </div>

        {!ready && s.otp_real_enabled && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 mt-0.5"/>
            <div>Fill in all three Twilio fields below — without them, OTP will fail and the wizard will continue to skip the OTP step.</div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Account SID"><Input data-testid="twilio-sid" value={s.twilio_account_sid || ""} onChange={e=>set("twilio_account_sid", e.target.value)}/></Field>
          <Field label="Auth Token"><Input data-testid="twilio-token" type="password" value={s.twilio_auth_token || ""} onChange={e=>set("twilio_auth_token", e.target.value)}/></Field>
          <Field label="From phone (Twilio number)"><Input data-testid="twilio-from" value={s.twilio_from_number || ""} onChange={e=>set("twilio_from_number", e.target.value)} placeholder="+1xxxxxxxxxx"/></Field>
        </div>

        <Button data-testid="twilio-save" className="btn-saffron rounded-full" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>} Save
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
